import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnDestroy, OnInit } from '@angular/core';
import { RightPanelService } from 'src/app/core/services/right-panel.service';
import { RightPanelType } from 'src/app/core/enums/right-panel-type.enum';
import { TicketService } from 'src/app/core/services/ticket/ticket.service';
import { finalize, takeUntil } from 'rxjs/operators';
import { NotificationsService } from 'angular2-notifications';
import { CommonDataService } from 'src/sb-tools/typescript/services/common-data.service';
import { TicketSource } from '@sb-tools/typescript/enums/ticket-source.enum';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Subject } from 'rxjs/internal/Subject';
import { SuperOfficeService } from '../../core/services/super-office.service';
import { SuperOfficeIntegrationModalComponent } from '../../shared/modal-windows/super-office-integration-modal/super-office-integration-modal.component';
import { BsModalService } from 'ngx-bootstrap/modal';
import { TicketDTO } from '../../core/models/dto/ticket.dto';
import { TooltipModule } from 'ngx-bootstrap/tooltip';

@Component({
    selector: 'app-integration-block',
    templateUrl: './integration-block.component.html',
    styleUrls: ['./integration-block.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [TooltipModule, TranslateModule],
})
export class IntegrationBlockComponent implements OnInit, OnDestroy {
    @Input() ticket: TicketDTO;

    fistMessageCreatorEmail: string;

    isIndigoAvailable: boolean;
    isSendingToIndigo: boolean;
    isSendingToServiceNow: boolean;
    isServiceNowAvailable: boolean;
    isSuperOfficeAvailable: boolean;
    isVoyadoAvailable: boolean;

    rightPanelTypeEnum = RightPanelType;
    voyadoTestEmails: string[] = [
        'anastasia@socialboards.com',
        'anton@socialboards.com',
        'maria@socialboards.com',
        'natalia@socialboards.com',
        'tanya@socialboards.com',
    ];

    private unsubscriber = new Subject<void>();

    constructor(
        public commonDataService: CommonDataService,
        public rightPanelService: RightPanelService,
        private changeDetectorRef: ChangeDetectorRef,
        private ticketService: TicketService,
        private notificationsService: NotificationsService,
        private translateService: TranslateService,
        private superOfficeService: SuperOfficeService,
        private bsModalService: BsModalService,
    ) {}

    ngOnInit() {
        this.isServiceNowAvailable =
            this.ticket.source !== TicketSource.chat &&
            ['seleniumtests', 'demo', 'rema1000', 'rema1000sandbox'].includes(this.commonDataService.community);

        this.isIndigoAvailable =
            (this.ticket.source === TicketSource.email ||
                this.ticket.source === TicketSource.form ||
                this.ticket.source === TicketSource.api) &&
            ['demo', 'brilleland'].includes(this.commonDataService.community);

        this.isSuperOfficeAvailable =
            (this.ticket.source === TicketSource.email ||
                this.ticket.source === TicketSource.form ||
                this.ticket.source === TicketSource.api) &&
            ['demo', 'berghansen'].includes(this.commonDataService.community);

        this.isVoyadoAvailable =
            this.commonDataService.socialMediaStatuses.voyadoCrm && !!this.ticket.firstMessageCreator.email;

        this.fistMessageCreatorEmail =
            this.voyadoTestEmails.indexOf(this.ticket.firstMessageCreator.email) > -1
                ? 'erh@jernia.no'
                : this.ticket.firstMessageCreator.email;
    }

    saveToSuperOffice() {
        const initialState = {
            confirmCallback: (loggedReplyModel) => {
                this.superOfficeService
                    .save(loggedReplyModel)
                    .pipe(takeUntil(this.unsubscriber))
                    .subscribe(() => {
                        this.ticket.isSentToSupperOffice = true;
                        this.changeDetectorRef.detectChanges();
                        this.notificationsService.success(
                            this.translateService.instant('Success!'),
                            this.translateService.instant('This ticket was successfully sent to SuperOffice.'),
                        );
                    });
            },
            ticketTitle: this.ticket.title,
            ticket: this.ticket,
        };

        this.bsModalService.show(SuperOfficeIntegrationModalComponent, {
            initialState,
            ignoreBackdropClick: true,
        });
    }

    sendToIndigo() {
        this.isSendingToIndigo = true;

        if (this.ticket.isSentToIndigo) {
            this.notificationsService.error(
                this.translateService.instant('Error!'),
                this.translateService.instant('Ticket was already sent to Indigo crm system'),
            );
            this.isSendingToIndigo = false;
            this.changeDetectorRef.detectChanges();
        } else {
            this.ticketService
                .sendToIndigo(this.ticket.id)
                .pipe(
                    finalize(() => {
                        this.isSendingToIndigo = false;
                        this.changeDetectorRef.detectChanges();
                    }),
                    takeUntil(this.unsubscriber),
                )
                .subscribe(() => {
                    this.ticket.isSentToIndigo = true;
                    this.notificationsService.success(
                        this.translateService.instant('Success!'),
                        this.translateService.instant('Ticket was successfully sent to Indigo crm system'),
                    );
                });
        }
    }

    sendToServicenow() {
        this.isSendingToServiceNow = true;
        this.ticketService
            .addServiceNowCase(this.ticket.id)
            .pipe(
                finalize(() => {
                    this.isSendingToServiceNow = false;
                    this.changeDetectorRef.detectChanges();
                }),
                takeUntil(this.unsubscriber),
            )
            .subscribe(() => {
                this.notificationsService.success(
                    this.translateService.instant('Success!'),
                    this.translateService.instant('Ticket was successfully sent to ServiceNow'),
                );
                this.ticket.isSentToServiceNow = true;
            });
    }

    ngOnDestroy() {
        this.unsubscriber.next();
        this.unsubscriber.complete();
    }
}
